import type {
  ResultRecord,
  RangedFieldTypes,
  QueryResult,
  ResultRecordValue,
  FieldType,
} from '@dynatrace-sdk/client-query';
import { formatDate } from '@dynatrace-sdk/units';

import {
  getFieldsArrayForRecordIndex,
  FieldEntry,
} from './getFieldsArrayForRecordIndex.js';
import { SingleValueData } from './types/single-value-data.js';

/**
 * @public
 */
export const EMPTY_SINGLE_VALUE_DATA: SingleValueData = {
  dimension: '',
  value: '',
};

/**
 * Converts a Grail record to a SingleValueData object that can be used for charting.
 *
 * @param fieldTypes - Types for fields
 * @param recordIndex - Index for the record
 * @param record - The Grail record
 * @param field - the user-select field to use from the record
 * @param autoscale - indicates if the value will be formatted with default formatter

 * @returns a SingleValueData object
 * @throws if the provided selected field is no longer part of the available fields of the provided record.
 * @public
*/
export function convertToSingleValueData(
  fieldTypes: RangedFieldTypes[],
  recordIndex: number,
  record: ResultRecord | null,
  field?: string,
  autoscale?: boolean,
): SingleValueData {
  const fields = getFieldsArrayForRecordIndex(fieldTypes, recordIndex);
  if (!record || fields.length === 0) {
    return EMPTY_SINGLE_VALUE_DATA;
  }

  let targetField: FieldEntry | undefined;
  if (field !== undefined) {
    targetField = fields?.find((currentField) => currentField.name === field);
  } else {
    targetField = fields[0]; // pick the first one by default
  }

  if (
    targetField === null ||
    targetField === undefined ||
    targetField.name === null ||
    targetField.name === undefined
  ) {
    throw new Error(`Selected field ${field} is not available.`);
  }

  type ConvertibleField = {
    recordValue: ResultRecordValue | undefined;
    recordTypeType: FieldType['type'];
    recordTypeName: string;
  };

  const target: ConvertibleField = {
    recordValue: record[targetField.name],
    recordTypeType: targetField.type,
    recordTypeName: targetField.name,
  };

  let value: string | number;

  if (autoscale) {
    switch (target.recordTypeType) {
      case 'boolean':
        value = Boolean(target.recordValue).toString();
        break;
      case 'timestamp':
        value = toFormattedTimestamp(target.recordValue as string);
        break;
      case 'long':
      case 'double':
        value = Number(target.recordValue);
        break;
      case 'string':
        value =
          typeof target.recordValue === 'string'
            ? (target.recordValue as string)
            : JSON.stringify(target.recordValue);
        break;
      default:
        value = JSON.stringify(target.recordValue);
        break;
    }
  } else {
    switch (target.recordTypeType) {
      case 'string':
        value = target.recordValue as string;
        break;
      case 'timestamp':
        value = target.recordValue as number;
        break;
      case 'long':
      case 'double':
        value = target.recordValue as string;
        break;
      default:
        value = JSON.stringify(target.recordValue);
    }
  }

  return {
    dimension: target.recordTypeName,
    value,
  };
}

/**
 * Lists the fields contained in a record.
 *
 * @param record - The input records from the query
 * @param fieldTypes - Types for fields
 * @param recordIndex -
 *
 * @public
 */
export function listRecordFields(
  fieldTypes: RangedFieldTypes[],
  recordIndex: number,
): Set<string> {
  const fields = getFieldsArrayForRecordIndex(fieldTypes, recordIndex);
  // no restriction on fields
  return new Set(fields.map((field) => field.name));
}

/**
 * Picks a record index given a record result array.
 * @param records - Records from a query
 * @public
 */
export function pickRecordIndex(records: QueryResult['records']): number {
  switch (records.length) {
    // if there are no records, return invalid index
    case 0:
      return -1;

    // if there is only one record, return it
    case 1:
      return 0;

    // if there are multiple records, pick the most recent (the last one).
    // The user can always change the ordering if another one is needed
    default:
      return records.length - 1;
  }
}

function toFormattedTimestamp(rawValue: string | number): string {
  if (typeof rawValue === 'string') {
    const value = Date.parse(rawValue);
    return isNaN(value)
      ? rawValue
      : formatDate(value, {
          dateStyle: 'short',
          timeStyle: 'medium',
        });
  }

  return formatDate(new Date(rawValue), {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}
